import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - g点网`;
    } else {
      document.title = '文章未找到 - g点网';
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400">
              <i className="ri-file-search-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被移除或链接有误。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = getRecentArticles(4).filter((a) => a.id !== article.id).slice(0, 3);

  const breadcrumbItems = [
    { label: category?.name || '', path: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    image: article.image,
    inLanguage: 'zh-CN',
    publisher: { '@type': 'Organization', name: 'g点网', url: 'https://gpoint.com' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://gpoint.com/article/${article.slug}` },
    keywords: article.tags.join(', '),
  };

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://gpoint.com/' },
      { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://gpoint.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={{
        name: article.title,
        url: `https://gpoint.com/article/${article.slug}`,
        description: article.excerpt,
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 pb-14">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs rounded-full bg-accent-100 text-accent-700">{tag}</span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} {t('views')}
                  </span>
                </div>
              </div>

              <div className="aspect-[16/9] rounded-xl overflow-hidden bg-secondary-100 mb-10">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
              </div>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:font-heading prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-base prose-p:leading-relaxed prose-p:mb-5 prose-p:text-foreground-700
                  prose-strong:text-foreground-900 prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {category && (
                <div className="mt-10 pt-6 border-t border-secondary-200">
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-sm text-foreground-600 hover:bg-secondary-200 transition-colors"
                  >
                    <i className={`${category.icon}`}></i>
                    返回 {category.name}
                  </Link>
                </div>
              )}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-14 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-950 mb-8">
                  {t('relatedArticles')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.slug}`}
                      className="group block p-4 rounded-lg bg-background-50 border border-secondary-100 hover:border-primary-200 transition-all"
                    >
                      <div className="aspect-[16/10] rounded-lg overflow-hidden bg-secondary-100 mb-3">
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">{a.title}</h4>
                      <p className="text-xs text-foreground-500 mt-2">{a.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}